<template>
  <v-card v-if="showView">
    <!-- Loading bar -->
    <v-progress-linear
      :active="isLoading"
      indeterminate
      absolute
      top
    ></v-progress-linear>
    <!-- Error message -->
    <v-alert
      v-model="showError"
      closable
      icon="$error"
      title="Error"
      type="error"
    >
      <div>{{ errorText }}</div>
    </v-alert>
    <!-- DATE PICKERS -->
    <v-row class="mx-2 mt-1">
      <v-col class="ma-0 pa-0">
        <v-card flat>
          <v-card-text>
            <!-- Date range picker -->
            <v-row justify="end">
              <v-col cols="6">
                <!-- Date preset select -->
                <v-select
                  :items="timeRangeItems"
                  item-value="value"
                  item-text="text"
                  v-model="selectedTimeRange"
                  :label="$t('common.filters.timeRange')"
                  clearable
                  @change="onTimeRange"
                  @click:clear="onClearTimeRange"
                  append-icon="mdi-chevron-down"
                ></v-select>
              </v-col>
              <v-col cols="6">
                <!-- Custom date -->
                <v-menu
                  ref="datePickerMenu"
                  v-model="datePickerMenu"
                  :close-on-content-click="false"
                  :return-value.sync="dates"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="dateRangeText"
                      readonly
                      :placeholder="$t('common.placeholders.selectDates')"
                      :disabled="selectedTimeRange !== 'custom'"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="dates" range>
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="onClearTimeRange"
                      >Cancel</v-btn
                    >
                    <v-btn
                      text
                      color="primary"
                      :disabled="
                        dates[0] === undefined || dates[1] === undefined
                      "
                      @click="$refs.datePickerMenu.save(dates)"
                      >OK</v-btn
                    >
                  </v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card-text class="ma-0 pa-0">
      <!-- Utilization per model widget -->
      <v-row no-gutters>
        <v-col>
          <v-card flat>
            <v-card-text>
              <Column
                :title="$t('fleetAnalytics.widgets.utilization')"
                :metricsData="utilizationPerModelData"
                :labels="barChartLabels"
                :treeFilter="true"
                :treeFilterName="segmentsFilterName"
                :treeItems="segmentFilterSelection"
                v-model="selectedSegments"
                @treeSelectionChanged="handleTreeSelection"
                @click:clear="onClearSegments"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Utilization per segment widget -->
      <v-row no-gutters>
        <v-col>
          <v-card flat>
            <v-card-text>
              <Column
                :title="$t('fleetAnalytics.widgets.segments')"
                :metricsData="utilizationPerSegmentData"
                :labels="barChartLabels"
                :segmentsFilter="false"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-divider></v-divider>
      <v-row no-gutters>
        <!-- Machines per model widget -->
        <v-col>
          <v-card flat>
            <v-card-text>
              <Pie
                :title="$t('fleetAnalytics.widgets.machinesPerModel')"
                :metricsData="machinesPerModelData"
                legendType="vertical"
                :total="machinesPerModelTotal"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Machines per segment widget -->
        <v-col>
          <v-card flat>
            <v-card-text>
              <Pie
                :title="$t('fleetAnalytics.widgets.machinesPerSegment')"
                :metricsData="machinesPerSegmentData"
                :legendFormatter="machinesPerSegmentLegendFormatter"
                legendType="horizontal"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import Pie from "@/components/widgets/Pie"
import Column from "@/components/widgets/Column"
export default {
  props: ["tenantUid"],
  components: { Pie, Column },
  data() {
    return {
      showError: false,
      errorText: null,
      isLoading: false,
      barChartLabels: { yaxis: "Time in use (h)", totalValue: " H" }, // Bar/Column charts
      //Segmentsfilter through charts
      segmentsFilterName: "Segments",
      segmentFilterSelection: [], // Selected items
      selectedSegments: [], // Selected items test with emits
      filterSelectedSegments: null,
      // Date pickers
      dates: [],
      datePickerMenu: false,
      selectedTimeRange: null,
      dateRangeText: "",
      timeRangeItems: [
        { text: this.$t("common.enums.day.today"), value: "today" },
        {
          text: `${this.$t("common.filters.last")} ${this.$tc(
            "common.units.day",
            7
          )}`,
          value: "7 days"
        },
        {
          text: `${this.$t("common.filters.last")} ${this.$tc(
            "common.units.day",
            30
          )}`,
          value: "30 days"
        },
        {
          text: `${this.$t("common.filters.last")} ${this.$tc(
            "common.units.day",
            90
          )}`,
          value: "90 days"
        },
        { text: this.$t("common.filters.customRange"), value: "custom" }
      ]
    }
  },
  async created() {
    await this.$store.dispatch("loadSegmentMetadata", "en")
    await this.$store.dispatch("fetchFleetAnalyticsDashboard", this.tenantUid)
    this.setDateRange()
    this.segmentFilterSelection = this.normalizedItems
  },
  computed: {
    showView() {
      return this.$store.getters.canReadFleetAnalytics
    },
    // Combine ID assignment and normalization to fill the TreeSelect
    normalizedItems() {
      return this.addIdsAndNormalize(this.$store.getters.segmentMetadata)
    },
    dashboardData() {
      return this.$store.getters.fleetAnalyticsDashboard ?? [] // If Dashboard returns empty e.g. no machines in tenant set as empty array
    },
    utilizationPerModelData() {
      let totalDataSet = this.dashboardData
      if (!Array.isArray(totalDataSet)) {
        return totalDataSet
      }
      // Check if any timerange filters exists to apply
      if (this.selectedTimeRange) {
        totalDataSet = this.filteredByTimeMachines(totalDataSet)
      }
      // Check if any segments filters exists to apply
      if (this.filterSelectedSegments) {
        totalDataSet = this.filteredByTimeAndSegmentsMachines(totalDataSet)
      }

      // Group machines by name
      let machinesByNames = Object.groupBy(
        totalDataSet,
        ({ productId }) => productId
      )

      let machinesThatHaveWorked = {}

      // Sum engine hours
      let labels = Object.keys(machinesByNames)
      for (let label of labels) {
        const sum = machinesByNames[label].reduce(
          (acc, cur) => acc + cur.latestEngineHours,
          0
        )

        // Filter machines with less than 1 engine hours
        if (sum > 1) {
          machinesThatHaveWorked[label] = sum
        }
      }
      return {
        labels: Object.keys(machinesThatHaveWorked),
        dataSet: Object.values(machinesThatHaveWorked)
      }
    },
    machinesPerModelTotal() {
      return this.dashboardData.length || 0
    },
    utilizationPerSegmentData() {
      let totalDataSet = this.dashboardData
      if (!Array.isArray(totalDataSet)) {
        return totalDataSet
      }
      // Check if no timerange reset to use dashboard default
      if (this.selectedTimeRange) {
        totalDataSet = this.filteredByTimeMachines(totalDataSet)
      }
      // Group machines by name
      let machinesBySegment = Object.groupBy(
        totalDataSet,
        ({ segmentName }) => segmentName
      )

      let machinesThatHaveWorked = {}

      // Sum engine hours
      let labels = Object.keys(machinesBySegment)
      for (let label of labels) {
        const sum = machinesBySegment[label].reduce(
          (acc, cur) => acc + cur.latestEngineHours,
          0
        )

        // Filter machines with less than 1 engine hours
        if (sum > 1) {
          machinesThatHaveWorked[label] = sum
        }
      }

      return {
        labels: Object.keys(machinesThatHaveWorked),
        dataSet: Object.values(machinesThatHaveWorked)
      }
    },
    machinesPerModelData() {
      const machinesQuantities = this.getMachinesPerModel()

      return {
        labels: Object.keys(machinesQuantities).map(
          m => `${m} (${machinesQuantities[m]})`
        ),
        dataSet: Object.values(machinesQuantities)
      }
    },
    machinesPerSegmentData() {
      if (!Array.isArray(this.dashboardData)) {
        return this.dashboardData
      }
      // Group machines by name
      let machinesBySegment = Object.groupBy(
        this.dashboardData,
        ({ segmentName }) => segmentName
      )

      let machinesQuantities = {}

      // Sum engine hours
      let labels = Object.keys(machinesBySegment)
      for (let label of labels) {
        const sum = machinesBySegment[label].length
        machinesQuantities[label] = sum
      }
      return {
        labels: Object.keys(machinesQuantities),
        dataSet: Object.values(machinesQuantities)
      }
    },
    selectedTenant() {
      return this.$store.getters.selectedTenantForRoute(this.$route.path)
    },
    selectedTenantUid() {
      // Return the selected tenant if selection is made else the logged in
      let tenantTreeSelect = this.selectedTenant
      let currentLoggedIn = this.tenantUid
      if (tenantTreeSelect) {
        return tenantTreeSelect.uid
      }
      return currentLoggedIn.uid
    }
  },
  methods: {
    showErrorMessage(msg) {
      this.showError = true
      this.errorText = msg
    },
    onTimeRange() {
      if (this.selectedTimeRange != "custom") {
        this.setDateRange()
      }
    },
    // Trigger segments API to update the date with the new filter selects
    async handleTreeSelection(newSelectedIds) {
      // Transform hierarchical IDs to structured selection
      this.filterSelectedSegments = newSelectedIds
      let selectedIds = this.convertSelectedSegmentsfunction(
        newSelectedIds,
        this.normalizedItems
      )

      try {
        this.isLoading = true
        await this.$store.dispatch("fetchMachinesSegments", {
          tenantUid: this.selectedTenantUid,
          segments: selectedIds
        })
      } catch (err) {
        this.showErrorMessage("faied to filter on segments " + err)
        this.isLoading = false
      }
      this.isLoading = false
    },
    // Transform hierarchical IDs to structured selection to send to API
    convertSelectedSegmentsfunction(filteredValues, source) {
      const result = []

      for (const path of filteredValues) {
        const parts = path.split(".").map(Number)

        const segmentId = parts[0]
        const categoryId = parts[1]
        const applicationIndex = parts[2]

        // CASE 1: only ["5"]
        if (parts.length === 1) {
          result.push({ segmentId: segmentId })
          continue
        }

        const segment = source.find(x => x.id == segmentId)
        if (!segment) {
          continue
        }

        const categories = segment.categories?.find(x => x.id == categoryId)
        if (!categories) {
          continue
        }

        let output = {
          segmentId: segmentId,
          categoryId: categoryId
        }

        // CASE 3: ["2.1.3"]
        if (parts.length === 3) {
          const value = categories.applications?.[applicationIndex - 1]
          if (value === undefined) {
            continue
          }
          output.applications = value
        }

        // CASE 2: ["2.1"] → do nothing extra
        result.push(output)
      }
      return result
    },
    // Recursive function: assign hierarchical IDs and normalize to {id, name, children}
    addIdsAndNormalize(items, path = "") {
      return items.map((item, index) => {
        const id = path ? `${path}.${index + 1}` : `${index + 1}`

        // Determine children: first level uses categories, second level uses applications
        let children = item.categories || item.applications
        // If children are strings (leaf nodes), convert them into objects
        if (children && typeof children[0] === "string") {
          children = children.map((name, i) => ({
            name,
            id: `${id}.${i + 1}`,
            children: []
          }))
        }

        return {
          ...item,
          id, // hierarchical ID
          name: item.name,
          children:
            children && children.length
              ? this.addIdsAndNormalize(children, id)
              : [] // empty array = treated as leaf by TreeSelect
        }
      })
    },
    filteredByTimeMachines(originalMachines) {
      // Filters by timerange
      let serialNosToKeep = this.$store.getters.machinesUtilizations

      return originalMachines
        .filter(oM => serialNosToKeep.some(n => n.serialNo === oM.serialNo))
        .map(oM => {
          const match = serialNosToKeep.find(n => n.serialNo === oM.serialNo)
          return {
            ...oM,
            latestEngineHours: Math.round(match.engineSeconds / 3600)
          }
        })
    },
    filteredByTimeAndSegmentsMachines(originalMachines) {
      let filteredBySegments = this.$store.getters.machinesSegments

      // Keep only the machines that exists in segments filter response
      if (this.filterSelectedSegments) {
        originalMachines = originalMachines.filter(fM =>
          filteredBySegments.some(n => n.serialNo == fM.serialNo)
        )
      }
      return originalMachines
    },
    getMachinesPerModel(segment) {
      let machines = this.dashboardData
      if (segment) {
        machines = machines.filter(
          m => (m.segmentName || "_") == (segment || "_")
        )
      }

      // Group machines by name
      let machinesByNames = Object.groupBy(
        machines,
        ({ productName }) => productName
      )

      let machinesQuantities = {}

      // Sum engine hours
      let labels = Object.keys(machinesByNames)
      for (let label of labels) {
        const sum = machinesByNames[label].length
        machinesQuantities[label] = sum
      }

      return machinesQuantities
    },
    machinesPerSegmentLegendFormatter(segmentName) {
      const groupedMachines = this.getMachinesPerModel(segmentName)
      const html = Object.keys(groupedMachines).reduce((acc, machineKey) => {
        return `${acc}
          <div style="display:flex; width:100%">
              <span style="flex:1">${machineKey}</span>
              <span style="flex:1; text-align:right;">(${groupedMachines[machineKey]})</span>
          </div>`
      }, "")
      const total = Object.keys(groupedMachines).reduce((acc, machineKey) => {
        return acc + groupedMachines[machineKey]
      }, 0)

      return `
        <div style="display:flex; flex-direction:column; height:100%">
          <h3>${segmentName}</h3>
          <div style="flex:3; align-content:center;">${html}</div>
          <h4>Total: ${total}</h4>
        </div>
      `
    },
    setDateRange() {
      let startDate = new Date()
      let offset = 0

      if (!this.selectedTimeRange) {
        this.dates = []
        return
      } else if (this.selectedTimeRange === "today") {
        offset = 0
      } else if (this.selectedTimeRange === "7 days") {
        offset = 6 // Becomes 7 since today will be included
      } else if (this.selectedTimeRange === "30 days") {
        offset = 29 // Becomes 30 since today will be included
      } else if (this.selectedTimeRange === "90 days") {
        offset = 89 // Becomes 90 since today will be included
      }

      startDate.setDate(startDate.getDate() - offset)
      this.dates = [
        startDate.toISOString().substring(0, 10),
        new Date().toISOString().substring(0, 10)
      ]
    },
    async fetchCustomMachinesUtilizations() {
      try {
        this.isLoading = true
        await this.$store.dispatch("fetchMachinesUtilizations", {
          tenantUid: this.selectedTenantUid,
          start: this.dates[0],
          end: this.dates[1]
        })
      } finally {
        this.isLoading = false
      }
    },
    async onClearTimeRange() {
      this.datePickerMenu = false
      this.selectedTimeRange = null
      this.dates = []
      await this.$store.dispatch(
        "fetchFleetAnalyticsDashboard",
        this.selectedTenantUid
      )
    },
    onClearSegments() {
      this.filterSelectedSegments = ""
      this.$store.dispatch(
        "fetchFleetAnalyticsDashboard",
        this.selectedTenantUid
      )
    }
  },
  watch: {
    selectedTimeRange(newRange) {
      // Display date picker. OK callback will trigger fetch
      if (newRange == "custom") {
        this.datePickerMenu = true
      }
    },
    dates(newDates) {
      // Always set earliest date as start date
      if (!newDates || newDates.length !== 2) {
        this.dateRangeText = ""
        return
      }
      if (newDates[0] > newDates[1]) {
        newDates.reverse()
      }
      this.dateRangeText = newDates.join(" ~ ")
      this.fetchCustomMachinesUtilizations()
    },
    async selectedTenantUid() {
      await this.$store.dispatch(
        "fetchFleetAnalyticsDashboard",
        this.selectedTenantUid
      )
    }
  }
}
</script>

<style type="scss">
.no-boxshadows {
  box-shadow: unset !important;
}

.fleet-analytics-header {
  background-color: #ffcd00 !important;
  color: black !important;
  text-transform: uppercase;
}
</style>
